import { useState } from "react";
import { Box, styled } from "@mui/material";
import { Outlet } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";

import { useMachine } from "@xstate/react";

import { useAppLayoutContext } from "../hooks/routingHooks";

import { mapPanelMachine, MapPanelState } from "../machines/MapPanelMachine";
import { MapContent } from "../components/map/MapContent";
import { LayerSearchControl } from "../components/map/LayerSearchControl";
import { LayerPointControl } from "../components/map/LayerPointControl";
import { MapMarker } from "../components/map/MapMarker";
import { SelectedWMSLayers } from "../components/map/SelectedWMSLayers";
import { LayersFallback } from "../components/boundary/LayersFallBack";
import { LegendControl } from "../components/map/LegendControl";
import { FullscreenControl } from "../components/map/FullscreenControl";
import { LayerPropertySelector } from "../components/layerProperties/LayerPropertySelector";
import { DefaultResetFallback } from "../components/boundary/DefaultResetFallback";
import { MapSearchControl } from "components/map/MapSearchControl";

export function MapLayout() {
  const { settings, config } = useAppLayoutContext();
  const [mapOverlayContainer, setMapOverlayContainer] =
    useState<HTMLDivElement | null>(null);
  const [drawerContainer, setDrawerContainer] = useState<Element | null>(null);
  const hasLayerProperties = settings.layerProperties.length > 0;

  const [state, send] = useMachine(mapPanelMachine);

  return (
    <Styles
      collapsedRightPanel={
        state.value !== "closed" &&
        typeof state.value !== "string" &&
        (state.value.point === "pointCollapsed" ||
          state.value.search === "searchCollapsed")
      }
      className={[
        state.value !== "closed" ? "with-search" : "without-search",
        hasLayerProperties ? "with-properties" : "without-properties",
      ].join(" ")}
    >
      {hasLayerProperties && (
        <LayerPropertySelector
          className="layer-property-selector"
          config={config}
          layerProperties={settings.layerProperties}
        />
      )}

      <MapContent
        className={[
          "map-content",
          state.value !== "closed" ? "with-search" : "without-search",
          hasLayerProperties ? "with-variants" : "without-variants",
        ].join(" ")}
        settings={settings}
      >
        <MapSearchControl type={settings.locationSearchType} />

        <ErrorBoundary FallbackComponent={LayersFallback}>
          {drawerContainer && (
            <LayerSearchControl
              config={config}
              layerProperties={settings.layerProperties}
              drawerContainer={drawerContainer}
              stateValue={state.value as MapPanelState}
              updateState={send}
            />
          )}
          {mapOverlayContainer && drawerContainer && (
            <LayerPointControl
              layerProperties={settings.layerProperties}
              overlayContainer={mapOverlayContainer}
              drawerContainer={drawerContainer}
              stateValue={state.value as MapPanelState}
              updateState={send}
            />
          )}

          <MapMarker />
          {mapOverlayContainer && (
            <SelectedWMSLayers
              layerProperties={settings.layerProperties}
              config={config}
              overlayContainer={mapOverlayContainer}
            />
          )}
          <ErrorBoundary FallbackComponent={DefaultResetFallback}>
            <LegendControl
              position="bottomleft"
              config={config}
              layerProperties={settings.layerProperties}
            />
          </ErrorBoundary>
          <FullscreenControl position="bottomright" config={config} />
        </ErrorBoundary>
      </MapContent>
      <div className="right-pane" ref={(ref) => setDrawerContainer(ref)}></div>

      <Outlet context={{ settings, config }} />
      <Box
        ref={(ref: HTMLDivElement) => setMapOverlayContainer(ref)}
        className={[
          "map-content overlay",
          state.value !== "closed" ? "with-search" : "without-search",
          hasLayerProperties ? "with-variants" : "without-variants",
        ].join(" ")}
      ></Box>
    </Styles>
  );
}

interface StyledProps {
  collapsedRightPanel: boolean;
}

const Styles = styled("main", {
  shouldForwardProp: (key) => key !== "collapsedRightPanel",
})<StyledProps>`
  grid-area: content;
  display: grid;
  grid-template-columns: ${({ theme, collapsedRightPanel }) =>
    theme.spacing(38, "1fr", !collapsedRightPanel ? 0 : 3)};
  grid-template-rows: ${({ theme }) => theme.spacing(7, "1fr")};
  grid-template-areas:
    "panel variant search"
    "panel map search";
  will-change: grid-template-columns;
  transition: grid-template-columns 320ms ease-in;
  overflow: hidden;
  max-height: calc(100vh - ${({ theme }) => theme.spacing(12)});

  &.with-search {
    grid-template-columns: ${({ theme, collapsedRightPanel }) =>
      theme.spacing(38, "1fr", !collapsedRightPanel ? 38 : 3)};
  }

  .layer-property-selector {
    grid-area: 1 / variant / 1 / search;
  }

  .map-content {
    grid-area: map;
    &.without-variants {
      grid-area: 1 / map / last-line / map;
      &.overlay {
        grid-area: 1 / map / last-line / map;
      }
    }

    &:not(.overlay) {
      z-index: 0;
    }
    &.overlay {
      z-index: 1;
      pointer-events: none;

      padding: ${({ theme }) => theme.spacing(2, 10)};
    }
  }

  & > *:not(.right-pane):not(.map-content):not(.layer-property-selector) {
    grid-area: 1 / panel / last-line / panel;
  }

  &.without-properties .right-pane {
    grid-area: 1 / search / last-line / search;
  }

  .right-pane {
    grid-area: 2 / search / last-line / search;
    display: flex;
    position: relative;

    .MuiDrawer-docked {
      flex: 1 1 auto;

      & > .MuiPaper-root {
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
      }
    }
  }
`;
